import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '@/services/apiClient';
import { formatCents } from '@/services/billingApi';
import {
  FEATURE_CATALOG,
  recruiterApi,
  type BillingType,
  type Feature,
  type FeatureInfo,
  type PlanInput,
  type RecruiterPlan,
} from '@/services/recruiterApi';
import { FeatureToggles } from './FeatureToggles';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'disabled';

interface FormState {
  id: string | null;
  name: string;
  description: string;
  priceDollars: string;
  billingType: BillingType;
  features: Feature[];
  adsPerStream: string;
  freeStreamsPerDay: string;
  screenLimit: string;
  upgradePlanId: string;
}

const EMPTY_FORM: FormState = {
  id: null, name: '', description: '', priceDollars: '', billingType: 'one_time',
  features: [], adsPerStream: '', freeStreamsPerDay: '', screenLimit: '1', upgradePlanId: '',
};

export function Plans(): JSX.Element {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<RecruiterPlan[]>([]);
  // Seed from the static catalog so the feature selector always renders, even
  // if the (best-effort) /features call never resolves. Refined below on load.
  const [features, setFeatures] = useState<FeatureInfo[]>([...FEATURE_CATALOG]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    // The plans list drives the load status. A 404 means the recruiter backend
    // is flag-disabled on this environment (only /enabled is mounted) — surface
    // that honestly rather than as a generic failure.
    try {
      const { plans } = await recruiterApi.listPlans(false);
      setPlans(plans);
      setStatus('ready');
    } catch (err) {
      setStatus(err instanceof ApiClientError && err.status === 404 ? 'disabled' : 'error');
      return;
    }
    // Feature labels are best-effort: keep the static catalog on any failure.
    try {
      const { features } = await recruiterApi.listFeatures();
      if (features?.length) setFeatures(features);
    } catch {
      /* keep the static FEATURE_CATALOG seeded above */
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const toForm = (p: RecruiterPlan): FormState => ({
    id: p.id, name: p.name, description: p.description ?? '',
    priceDollars: (p.priceCents / 100).toFixed(2), billingType: p.billingType,
    features: [...p.features], adsPerStream: p.adConfig?.adsPerStream != null ? String(p.adConfig.adsPerStream) : '',
    freeStreamsPerDay: p.adConfig?.freeStreamsPerDay != null ? String(p.adConfig.freeStreamsPerDay) : '',
    screenLimit: String(p.screenLimit), upgradePlanId: p.upgradePlanId ?? '',
  });

  // Same-billing-type, non-archived, own plans (excluding the one being edited).
  const parentOptions = useMemo(
    () => (form ? plans.filter((p) => p.id !== form.id && p.billingType === form.billingType) : []),
    [plans, form],
  );

  // Warn if a chosen parent's billingType differs (shouldn't happen given the
  // filtered options, but a stale selection after a billingType switch can).
  const chosenParent = form ? plans.find((p) => p.id === form.upgradePlanId) : undefined;
  const showMixedWarning = Boolean(chosenParent && form && chosenParent.billingType !== form.billingType);

  function buildInput(f: FormState): PlanInput {
    const adConfig: { adsPerStream?: number; freeStreamsPerDay?: number } = {};
    if (f.adsPerStream.trim()) adConfig.adsPerStream = Number(f.adsPerStream);
    if (f.freeStreamsPerDay.trim()) adConfig.freeStreamsPerDay = Number(f.freeStreamsPerDay);
    return {
      name: f.name.trim(),
      description: f.description.trim() || null,
      priceCents: Math.round(Number(f.priceDollars || '0') * 100),
      billingType: f.billingType,
      features: f.features,
      adConfig: Object.keys(adConfig).length ? adConfig : null,
      screenLimit: Number(f.screenLimit || '1'),
      upgradePlanId: f.upgradePlanId || null,
    };
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      const input = buildInput(form);
      if (form.id) await recruiterApi.updatePlan(form.id, input);
      else await recruiterApi.createPlan(input);
      setForm(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recruitment.common.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive(id: string): Promise<void> {
    if (!window.confirm(t('recruitment.plans.confirmArchive'))) return;
    try { await recruiterApi.archivePlan(id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t('recruitment.common.actionFailed')); }
  }

  function toggleFeature(key: Feature): void {
    setForm((f) => (f ? { ...f, features: f.features.includes(key) ? f.features.filter((x) => x !== key) : [...f.features, key] } : f));
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('recruitment.plans.title')}</h1>
        <p className="muted">{t('recruitment.plans.intro')}</p>
      </header>

      <section className="admin-card">
        {status === 'loading' && <p className="muted" role="status">{t('recruitment.common.loading')}</p>}
        {status === 'error' && <p className="error" role="alert">{t('recruitment.common.actionFailed')}</p>}
        {status === 'disabled' && <p className="muted" role="status">{t('recruitment.common.disabled')}</p>}
        {status === 'ready' && plans.length === 0 && <p className="muted">{t('recruitment.plans.empty')}</p>}
        {status === 'ready' && plans.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('recruitment.plans.table.name')}</th>
                  <th scope="col" className="num">{t('recruitment.plans.table.price')}</th>
                  <th scope="col">{t('recruitment.plans.table.billing')}</th>
                  <th scope="col" className="num">{t('recruitment.plans.table.screens')}</th>
                  <th scope="col" aria-label={t('recruitment.plans.table.actions')} />
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td className="num">{formatCents(p.priceCents)}</td>
                    <td>{p.billingType === 'monthly' ? t('recruitment.plans.form.monthly') : t('recruitment.plans.form.oneTime')}</td>
                    <td className="num">{p.screenLimit}</td>
                    <td className="actions">
                      <button type="button" className="btn-ghost" onClick={() => setForm(toForm(p))}>{t('recruitment.common.edit')}</button>
                      <button type="button" className="btn btn-outline" onClick={() => void handleArchive(p.id)}>{t('recruitment.common.archive')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!form && status !== 'disabled' && (
          <button type="button" className="btn" style={{ marginTop: 12 }} onClick={() => setForm({ ...EMPTY_FORM })}>
            {t('recruitment.plans.newPlan')}
          </button>
        )}
      </section>

      {form && (
        <section className="admin-card">
          <h2 className="admin-card__title">{form.id ? t('recruitment.plans.editPlan') : t('recruitment.plans.newPlan')}</h2>
          <form className="recruiter-form" onSubmit={handleSubmit} noValidate>
            <div className="recruiter-form__grid">
              <div className="field">
                <label htmlFor="pl-name">{t('recruitment.plans.form.name')}</label>
                <input id="pl-name" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="pl-price">{t('recruitment.plans.form.price')}</label>
                <input id="pl-price" type="number" min="0" step="0.01" value={form.priceDollars} onChange={(e) => setForm({ ...form, priceDollars: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="pl-billing">{t('recruitment.plans.form.billingType')}</label>
                <select id="pl-billing" value={form.billingType} onChange={(e) => setForm({ ...form, billingType: e.target.value as BillingType, upgradePlanId: '' })}>
                  <option value="one_time">{t('recruitment.plans.form.oneTime')}</option>
                  <option value="monthly">{t('recruitment.plans.form.monthly')}</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="pl-screens">{t('recruitment.plans.form.screenLimit')}</label>
                <input id="pl-screens" type="number" min="1" value={form.screenLimit} onChange={(e) => setForm({ ...form, screenLimit: e.target.value })} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="pl-desc">{t('recruitment.plans.form.description')}</label>
              <input id="pl-desc" value={form.description} maxLength={500} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="field">
              <span className="field__label" id="pl-features-label">{t('recruitment.plans.form.features')}</span>
              <FeatureToggles features={features} selected={form.features} onToggle={toggleFeature} />
            </div>

            <fieldset className="recruiter-form__group">
              <legend className="field__label">{t('recruitment.plans.form.ads')}</legend>
              <div className="recruiter-form__grid">
                <div className="field">
                  <label htmlFor="pl-ads">{t('recruitment.plans.form.adsPerStream')}</label>
                  <input id="pl-ads" type="number" min="0" value={form.adsPerStream} onChange={(e) => setForm({ ...form, adsPerStream: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="pl-free">{t('recruitment.plans.form.freeStreamsPerDay')}</label>
                  <input id="pl-free" type="number" min="0" value={form.freeStreamsPerDay} onChange={(e) => setForm({ ...form, freeStreamsPerDay: e.target.value })} />
                </div>
              </div>
              <small className="muted">{t('recruitment.plans.form.adsHint')}</small>
            </fieldset>

            <div className="field">
              <label htmlFor="pl-parent">{t('recruitment.plans.form.upgradeParent')}</label>
              <select id="pl-parent" value={form.upgradePlanId} onChange={(e) => setForm({ ...form, upgradePlanId: e.target.value })}>
                <option value="">{t('recruitment.plans.form.upgradeNone')}</option>
                {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {showMixedWarning && (
                <p className="error" role="alert">
                  {t('recruitment.plans.form.mixedTypeWarning')} <Link to="/recruitment/docs">{t('recruitment.plans.form.docsLink')}</Link>
                </p>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn" disabled={busy}>{form.id ? t('recruitment.common.save') : t('recruitment.common.create')}</button>
              <button type="button" className="btn-ghost" onClick={() => setForm(null)}>{t('recruitment.common.cancel')}</button>
            </div>
            {error && <p className="error" role="alert">{error}</p>}
          </form>
        </section>
      )}
    </main>
  );
}
